import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

export function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    // Move keyboard focus back to the top of the page so screen-reader and
    // keyboard users land at the start of the document, not on the button.
    const target =
      (document.querySelector("main") as HTMLElement | null) ??
      (document.querySelector("h1") as HTMLElement | null) ??
      document.body;
    if (target) {
      const hadTabIndex = target.hasAttribute("tabindex");
      if (!hadTabIndex) target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
      if (!hadTabIndex) {
        target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
      }
    }
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Back to top"
      title="Back to top"
      aria-hidden={visible ? undefined : "true"}
      tabIndex={visible ? 0 : -1}
      className={cn(
        "back-to-top-button fixed bottom-6 right-6 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full demo-button-primary shadow-lg transition-all duration-300 hover:scale-110",
        "focus:outline-none focus-visible:outline-none",
        "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "motion-reduce:transition-none motion-reduce:hover:scale-100",
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0",
      )}
    >
      <ArrowUp className="h-5 w-5" aria-hidden="true" focusable="false" />
      <span className="sr-only">Back to top of page</span>
    </button>
  );
}
